import NewModal from "../../shared/Modals/NewModal";
import { useTranslation } from "react-i18next";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import crudApi from "@serie3/common/api/crudApi";
import { useState } from "react";
import { useFormik } from "formik";
import PostForm from "@serie3/mymediaplanner/Modules/Social/Forms/PostForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck, faEye, faHeart, faBullseye,
  faWandMagicSparkles, faSave, faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

const GOALS = [
  { key: "reach",      icon: faEye,      color: "#038edc", label: "Reach",      description: "Maximise impressions and new audience exposure" },
  { key: "conversion", icon: faBullseye, color: "#f1734f", label: "Conversion",  description: "Drive clicks, sign-ups or direct responses" },
  { key: "brand",      icon: faHeart,    color: "#564ab1", label: "Brand",       description: "Build trust, authority and long-term awareness" },
];

const POST_TYPES = [
  { value: "Text",           label: "Backend.Post.PostType.Text" },
  { value: "Text with Image",label: "Backend.Post.PostType.TextImage" },
  { value: "Carousel Post",  label: "Backend.Post.PostType.Carousel" },
  { value: "Video",          label: "Backend.Post.PostType.Video" },
  { value: "Shortform Video",label: "Backend.Post.PostType.Shortform" },
  { value: "Storie Video",   label: "Backend.Post.PostType.Stories" },
  { value: "Poll",           label: "Backend.Post.PostType.Poll" },
];

const STEPS = [
  { label: "Purpose" },
  { label: "Type" },
  { label: "Platform" },
];

const StepBar = ({ current }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28 }}>
    {STEPS.map((_, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "none" }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: done ? "#51d28c" : active ? "#038edc" : "#eaecf0",
            color: done || active ? "#fff" : "#9299a8",
            fontSize: 11, fontWeight: 700, transition: "background 0.2s",
          }}>
            {done ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} /> : i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, background: done ? "#51d28c" : "#eaecf0", transition: "background 0.2s" }} />
          )}
        </div>
      );
    })}
  </div>
);

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#9299a8", marginBottom: 16 }}>
    {children}
  </p>
);

const VariantsModal = ({
  show,
  setShow,
  post,
  dataList,
  setDataList,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState(null);
  const [postType, setPostType] = useState(null);
  const [variantText, setVariantText] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const editPostForm = PostForm.createPostForm(dataList, setDataList, null, true);
  const formik = useFormik(editPostForm);

  const reset = () => {
    setStep(0);
    setGoal(null);
    setPostType(null);
    setVariantText("");
    setShowSpinner(false);
    setAccepted(false);
    formik.resetForm();
  };

  const generate = async (type, currentGoal) => {
    setShowSpinner(true);
    setVariantText("");
    setAccepted(false);
    formik.resetForm();
    formik.setFieldValue("content_type", currentGoal);
    formik.setFieldValue("post_type", type);
    formik.setFieldValue("parent.id", post.id);
    formik.setFieldValue("title", t("Backend.Variants.TitlePrefix") + post.title);

    const response = await crudApi.justGet(
      SocialEndpoints.posts.ai.createTypeVariant + post.id + "/" + type
    );
    setShowSpinner(false);
    if (response.ok) {
      setVariantText(response.data.post ?? response.data);
    }
  };

  const NavButtons = () => (
    <div style={{ display: "flex", gap: 8 }}>
      {step > 0 && (
        <button
          type="button"
          onClick={() => setStep(step - 1)}
          style={{
            width: 36, height: 36, borderRadius: 50, border: "1.5px solid #e2e5ea",
            background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 13 }} />
        </button>
      )}
      {step === 2 && (
        <button
          type="button"
          onClick={() => {
            setStep(3);
            generate(postType, goal);
          }}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "none",
            background: "linear-gradient(135deg,#564ab1,#3d3490)",
            color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(86,74,177,0.35)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          {t("Backend.Variants.Generate")}
        </button>
      )}
      {step === 3 && variantText && !showSpinner && (
        <button
          type="button"
          disabled={accepted}
          onClick={async () => {
            formik.setFieldValue("text", variantText);
            await formik.submitForm();
            setAccepted(true);
          }}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "none",
            background: accepted
              ? "#9299a8"
              : "linear-gradient(135deg,#51d28c,#2da866)",
            color: "#fff", fontSize: 14, fontWeight: 600,
            cursor: accepted ? "default" : "pointer",
            boxShadow: accepted ? "none" : "0 4px 14px rgba(81,210,140,0.35)",
            display: "flex", alignItems: "center", gap: 8,
            transition: "background 0.2s",
          }}
        >
          <FontAwesomeIcon icon={faSave} />
          {t("Backend.Post.Variants.AcceptPostType")}
        </button>
      )}
    </div>
  );

  return (
    <NewModal
      show={show}
      setShow={setShow}
      title={t("Backend.Post.Variants.Title")}
      size="xl"
      onShow={reset}
      onHide={reset}
      ExtraButton={NavButtons}
    >
      <StepBar current={step > 2 ? 3 : step} />

      {/* Step 0 — Purpose */}
      {step === 0 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChooseGoal")}</SectionLabel>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {GOALS.map(({ key, icon, color, label, description }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setGoal(key); setStep(1); }}
                style={{
                  flex: "1 1 180px", padding: "22px 20px", borderRadius: 14,
                  border: "2px solid #e2e5ea", background: "#fff",
                  cursor: "pointer", textAlign: "left", transition: "border-color 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e5ea"; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12, marginBottom: 14,
                  background: `${color}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FontAwesomeIcon icon={icon} style={{ color, fontSize: 20 }} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{description}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Step 1 — Post Type */}
      {step === 1 && (
        <>
          <SectionLabel>{t("Backend.Variants.ChooseFormat")}</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {POST_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => { setPostType(type.value); setStep(2); }}
                style={{
                  flex: "1 1 180px", padding: "16px 20px", borderRadius: 12,
                  border: "2px solid #e2e5ea", background: "#fff",
                  cursor: "pointer", textAlign: "left",
                  transition: "border-color 0.15s ease, background 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#038edc";
                  e.currentTarget.style.background = "rgba(3,142,220,0.03)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e5ea";
                  e.currentTarget.style.background = "#fff";
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{t(type.label)}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Step 2 — Platform */}
      {step === 2 && (
        <>
          <SectionLabel>{t("Backend.Variants.Platform")}</SectionLabel>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <button
              type="button"
              style={{
                flex: "0 0 160px", padding: "24px 20px", borderRadius: 14,
                border: "2px solid #0077b5", background: "rgba(0,119,181,0.04)",
                cursor: "default", textAlign: "center",
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 12, margin: "0 auto 12px",
                background: "rgba(0,119,181,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <FontAwesomeIcon icon={faLinkedin} style={{ color: "#0077b5", fontSize: 24 }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>LinkedIn</div>
              <div style={{ marginTop: 6 }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: "#0077b5", fontSize: 12 }} />
              </div>
            </button>
          </div>
        </>
      )}

      {/* Step 3 — Result */}
      {step === 3 && (
        <>
          {showSpinner && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px 0", color: "#9299a8" }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%",
                border: "2px dotted #564ab1", animation: "dotted-spin 0.9s linear infinite",
              }} />
              {t("Backend.Post.AIworking")}
            </div>
          )}
          {!showSpinner && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <SectionLabel>{t("Backend.Variants.Original")}</SectionLabel>
                <div
                  style={{
                    padding: "20px 24px", borderRadius: 12,
                    background: "#f9fafb", border: "1px solid #eaecf0",
                    fontSize: 14, lineHeight: 1.8, color: "#374151",
                  }}
                >
                  <span style={{ whiteSpace: "pre-line" }}>{post?.text}</span>
                </div>
              </div>
              <div>
                <SectionLabel>{t("Backend.Variants.AIVariant")}</SectionLabel>
                {variantText ? (
                  <div
                    style={{
                      padding: "20px 24px", borderRadius: 12,
                      background: "linear-gradient(135deg,rgba(86,74,177,0.05) 0%,rgba(86,74,177,0.02) 100%)",
                      border: "1px solid rgba(86,74,177,0.15)",
                      fontSize: 14, lineHeight: 1.8, color: "#374151",
                    }}
                  >
                    <span style={{ whiteSpace: "pre-line" }}>{variantText}</span>
                  </div>
                ) : (
                  <div style={{ color: "#9299a8", fontSize: 14, padding: "20px 0" }}>
                    {t("Backend.Variants.NoVariant")}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </NewModal>
  );
};

export default VariantsModal;
